import { createTheme, drawerClasses, type PaletteMode } from '@mui/material'
import type { Shadows } from '@mui/material/styles'
// import below is required to customize the DataGrid theme
// see https://mui.com/x/react-data-grid/getting-started/#typescript
// oxlint-disable-next-line no-empty-named-blocks require-module-specifiers
import type {} from '@mui/x-data-grid-premium/themeAugmentation'

declare module '@mui/material/styles' {
	interface Palette {
		ai: {
			main: string
			contrastText: string
		}
		tableColors: {
			row: string
			header: string
		}
	}
	interface PaletteOptions {
		ai?: {
			main: string
			contrastText: string
		}
		tableColors?: {
			row: string
			header: string
		}
	}
}

export default function GetTheme(mode: PaletteMode) {
	const isDark = mode === 'dark'
	const palette = {
		mode,
		primary: {
			main: isDark ? '#44C571' : '#11813D',
			contrastText: '#FFF',
		},
		secondary: {
			main: 'rgb(216, 158, 71)',
			contrastText: '#FFF',
		},
		info: {
			main: isDark ? '#44C571' : '#9BE8B6',
			contrastText: '#FFF',
		},
		success: {
			main: isDark ? '#44C571' : '#0E6533',
			contrastText: '#FFF',
		},
		warning: {
			main: isDark ? '#EDBF2F' : '#BB8211',
			contrastText: '#FFF',
		},
		numberUnit: {
			main: '#F00',
			contrastText: '#979797',
		},
		ai: {
			main: '#A181FA',
			contrastText: '#FFF',
		},
		background: {
			default: isDark ? '#131213' : '#F7F7F0',
			paper: isDark ? '#1e1e1e' : '#EFF0E0',
			badge: isDark ? '#2B2D2B' : '#616161',
		},
		text: {
			primary: isDark ? '#F4F4F4' : '#1A1A1A',
			secondary: isDark ? '#CDCDCD' : '#525252',
			disabled: isDark ? '#646865' : '#9E9E9E',
		},
		divider: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
		action: {
			hover: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
			selected: isDark ? 'rgba(68, 197, 113, 0.1)' : 'rgba(17, 129, 61, 0.1)',
			disabled: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.26)',
			disabledBackground: isDark
				? 'rgba(255,255,255,0.06)'
				: 'rgba(0,0,0,0.06)',
		},
		emissionTypes: {
			waste: '#D89E47',
			water: '#58C0C0',
			co2: '#44C571',
			spend: '#BF78CB',
			volume: '#296D62',
		},
		backgroundColors: {
			core: '#44C571',
			grey: isDark ? '#2B2D2B' : '#EBEBEB',
			darkGrey: isDark ? '#2F2F2F' : '#E7E8D1',
			opaque: isDark ? 'rgb(19, 14, 18)' : '#F5F5F5',
			dark: 'rgba(0, 0, 0, 0.8)',
			darkBlue: isDark ? '#222A3A' : '#EEF2FF',
			opaqueTransparent: isDark
				? 'rgba(19, 14, 18, 0.5)'
				: 'rgba(245, 245, 245, 0.5)',
			black: '#000',
			blue: '#2d56e7',
			errorLight: isDark ? '#71140f' : '#FCCBCA',
			greenLight: isDark ? '#11813D' : '#9BE8B6',
			lighterGrey: isDark ? '#ffffff14' : '#0000000f',
			warningLight: isDark ? '#4A2800' : '#FFE0B2',
			info: '#5FA9F2',
			cdp: '#d31144',
		},
		borderColors: {
			gray: ' #B0B0B0',
			darkGray: isDark ? '#585D59' : '#9E9E9E',
			darkestGray: isDark ? '#2B2D2B' : '#E0E0E0',
			greenishgrey: isDark ? '#697a6d' : '#8A9E8E',
			darkBlur: isDark ? '#404441' : '#C8CCC8',
			lightGrey: isDark ? '#393939' : '#E0E0E0',
			transparentWhite: isDark
				? 'rgba(255, 255, 255, 0.2)'
				: 'rgba(0,0,0,0.15)',
			error: '#E34F4F',
		},
		elementColors: {
			bad: isDark ? '#FE9696' : '#A11B15',
			good: isDark ? '#72E58B' : '#0E6533',
			warningLight: '#C0EA6F',
			warning: '#FFEB59',
			warningHeavy: isDark ? '#FB8C00' : '#E65100',
		},
		modalColors: {
			headerBackgroundColor: isDark ? '#3a3a3a' : '#F0F0F0',
			backgroundColor: isDark ? '#282828' : '#F7F7F0',
			borderColor: isDark ? '#525252' : '#E0E0E0',
			stepColor: isDark ? '#EDEDED' : '#333333',
			stepIconColor: isDark ? '#D9D9D9' : '#666666',
			stepBorderColor: isDark ? '#525252' : '#E0E0E0',
		},
		icons: {
			primary: isDark ? '#F4F4F4' : '#1A1A1A',
			secondary: isDark ? '#CDCDCD' : '#525252',
		},
		fonts: {
			primary: isDark ? '#F4F4F4' : '#1A1A1A',
			secondary: isDark ? '#CDCDCD' : '#525252',
			tertiary: isDark ? '#8D8D8D' : '#6B7280',
			white: '#FFF',
			darkGrey: '#6F6F6F',
			lightGrey: isDark ? '#CECECE' : '#6B7280',
			green: '#44C571',
			warning: '#ffa726',
			black: '#0D0D0D',
			error: '#E34F4F',
			disabled: isDark ? '#646865' : '#9E9E9E',
		},
		statuses: {
			inProgress: 'black',
			notStarted: '#393939',
			requested: '#5752BA',
			pendingReview: '#3E5BC1',
			completed: '#319457',
			rejected: '#92382C',
			declined: '#FF3838',
			unknown: 'grey',
		},
		stackBarColors: {
			grey1: '#DFDFDF',
			grey2: '#D1D1D1',
			grey3: '#ABABAB',
			grey4: '#8E8E8E',
			grey5: '#686868',
		},
		tableColors: {
			header: isDark ? '#393939' : '#F0F0F0',
			row: isDark ? '#262626' : '#FFFFFF',
		},
	}
	return createTheme({
		spaces: {
			sm: '0.25rem',
			m: '0.5rem',
			l: '0.875rem',
			xl: '1.25rem',
		},
		palette,
		shape: {
			borderRadius: 4,
		},
		shadows: (isDark
			? [
					'none',
					'0 1px 2px rgba(0,0,0,0.4)',
					'0 2px 4px rgba(0,0,0,0.4)',
					'0 4px 8px rgba(0,0,0,0.35)',
					'0 6px 12px rgba(0,0,0,0.3)',
					'0 8px 16px rgba(0,0,0,0.25)',
					...Array.from({ length: 19 }, () => '0 8px 16px rgba(0,0,0,0.25)'),
				]
			: [
					'none',
					'0 1px 2px rgba(0,0,0,0.08)',
					'0 2px 4px rgba(0,0,0,0.08)',
					'0 4px 8px rgba(0,0,0,0.06)',
					'0 6px 12px rgba(0,0,0,0.05)',
					'0 8px 16px rgba(0,0,0,0.04)',
					...Array.from({ length: 19 }, () => '0 8px 16px rgba(0,0,0,0.04)'),
				]) as Shadows,
		typography: {
			fontFamily: "'Inter', sans-serif",
			h1: {
				fontSize: '1.75rem',
				fontWeight: 600,
				letterSpacing: -0.5,
				lineHeight: 1.3,
			},
			h2: {
				fontSize: '1.125rem',
				fontWeight: 600,
				lineHeight: 1.3,
			},
			h3: {
				fontSize: '0.8125rem',
				fontWeight: 600,
				lineHeight: 1.4,
			},
			h4: {
				fontWeight: 600,
				fontSize: '1.75rem',
				lineHeight: 1.3,
				letterSpacing: -0.25,
			},
			h5: {
				fontWeight: 600,
				fontSize: '1.375rem',
				lineHeight: 1.3,
			},
			h6: {
				fontWeight: 700,
				fontSize: '1.125rem',
				lineHeight: 1.3,
			},
			subtitle1: {
				fontWeight: 600,
				size: '1.125rem',
				lineHeight: 1.4,
				letterSpacing: 0.15,
			},
			body1: {
				fontWeight: 400,
				fontSize: '0.8125rem',
				lineHeight: 1.5,
				letterSpacing: 0,
			},
			body2: {
				fontWeight: 450,
				fontSize: '0.8125rem',
				lineHeight: 1.5,
				letterSpacing: 0,
			},
			description: {
				color: palette.text.secondary,
				fontWeight: 400,
				fontSize: '0.8125rem',
			},
			button: {
				textTransform: 'none',
				fontWeight: 600,
				fontSize: '0.8125rem',
				letterSpacing: 0,
				lineHeight: 1.5,
			},
		},
		components: {
			MuiDataGrid: {
				styleOverrides: {
					root: {
						border: 'none',
						'& .MuiDataGrid-row': {
							backgroundColor: palette.background.paper,
						},
						'& .MuiDataGrid-column': {},
						'& .MuiDataGrid-cell': {
							borderBottom: `1px solid ${palette.divider}`,
							borderRight: `1px solid ${palette.divider}`,
						},

						'& .MuiDataGrid-columnHeader': {
							backgroundColor: isDark
								? 'rgba(255,255,255,0.04)'
								: 'rgba(0,0,0,0.03)',
							borderBottom: `1px solid ${palette.divider}`,
							borderRight: `1px solid ${palette.divider}`,
							padding: '1rem',
						},
						'& .MuiDataGrid-footerContainer': {
							backgroundColor: palette.background.paper,
							borderRadius: '0 0 0.25rem',
						},
						'& .MuiDataGrid-columnHeaderTitle': {
							fontWeight: 600,
						},
						'& .MuiDataGrid-withBorderColor': {
							borderColor: palette.divider,
						},
						'& .MuiDataGrid-columnHeader--pinnedRight': {
							backgroundColor: palette.background.paper,
							backgroundImage: isDark
								? 'linear-gradient(rgba(255,255,255,0.04), rgba(255,255,255,0.04))'
								: 'linear-gradient(rgba(0,0,0,0.03), rgba(0,0,0,0.03))',
						},
						'& .MuiDataGrid-scrollbarFiller--pinnedRight': {
							backgroundColor: palette.background.paper,
						},
					},
				},
			},

			MuiTabs: {
				styleOverrides: {
					flexContainer: {
						display: 'inline-block',
					},
				},
			},
			MuiModal: {
				styleOverrides: {
					root: {
						'.MuiBackdrop-root': {
							backgroundColor: 'rgba(0, 0, 0, 0.65)',
							backdropFilter: 'blur(4px)',
						},
					},
				},
			},
			MuiPopover: {
				styleOverrides: {
					root: {
						'.MuiBackdrop-root': {
							backgroundColor: 'transparent',
							backdropFilter: 'none',
						},
					},
					paper: {
						maxWidth: '50%',
					},
				},
			},
			MuiTooltip: {
				styleOverrides: {
					tooltip: {
						backgroundColor: isDark ? '#2c2c2c' : '#f5f5f5',
						color: isDark ? '#c6c6c6' : '#1A1A1A',
						fontSize: '0.75rem',
						border: isDark ? 'none' : '1px solid rgba(0,0,0,0.12)',
					},
				},
			},

			MuiDrawer: {
				styleOverrides: {
					root: {
						zIndex: 1300,
						position: 'absolute',
						[`.${drawerClasses.paper}`]: {
							backdropFilter: 'blur(3px)',
							backgroundColor: isDark
								? 'rgb(0, 0, 0, 0.8)'
								: 'rgba(255, 255, 255, 0.9)',
						},
					},
				},
			},

			MuiInputLabel: {
				defaultProps: {
					style: {
						fontSize: '1rem',
						lineHeight: '1.4375em',
					},
				},
			},
			MuiOutlinedInput: {
				styleOverrides: {
					notchedOutline: {
						'& legend': {
							fontSize: '0.75rem',
						},
					},
				},
			},
			MuiTextField: {
				defaultProps: {
					variant: 'outlined',
					size: 'small',
				},
				styleOverrides: {
					root: {
						'& input[type=number]': {
							MozAppearance: 'textfield',
						},
						'& input[type=number]::-webkit-outer-spin-button': {
							WebkitAppearance: 'none',
							margin: 0,
						},
						'& input[type=number]::-webkit-inner-spin-button': {
							WebkitAppearance: 'none',
							margin: 0,
						},
					},
				},
			},
			MuiTablePagination: {
				styleOverrides: {
					root: {
						background: palette.background.paper,
						borderRadius: '0 0 0.25rem',
					},
				},
			},
			MuiDialog: {
				styleOverrides: {
					paper: {
						overflowY: 'hidden',
						backgroundColor: palette.background.default,
					},
				},
			},
			MuiTab: {
				styleOverrides: {
					root: {
						backgroundColor: 'transparent',
						alignItems: 'flex-start',
						minWidth: '200px',
					},
				},
			},
			MuiChip: {
				styleOverrides: {
					root: {
						margin: '5px 5px 5px 0',
					},
					colorPrimary: {
						'&.MuiChip-filled': {
							color: isDark ? 'rgba(0,0,0,0.87)' : '#FFF',
							'& .MuiChip-icon, & .MuiChip-deleteIcon': {
								color: isDark ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.7)',
							},
						},
					},
					colorSecondary: {
						'&.MuiChip-filled': {
							color: isDark ? 'rgba(0,0,0,0.87)' : '#FFF',
							'& .MuiChip-icon, & .MuiChip-deleteIcon': {
								color: isDark ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.7)',
							},
						},
					},
					colorSuccess: {
						'&.MuiChip-filled': {
							color: isDark ? 'rgba(0,0,0,0.87)' : '#FFF',
							'& .MuiChip-icon, & .MuiChip-deleteIcon': {
								color: isDark ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.7)',
							},
						},
					},
					colorError: {
						'&.MuiChip-filled': {
							color: isDark ? 'rgba(0,0,0,0.87)' : '#FFF',
							'& .MuiChip-icon, & .MuiChip-deleteIcon': {
								color: isDark ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.7)',
							},
						},
					},
					colorWarning: {
						'&.MuiChip-filled': {
							color: isDark ? 'rgba(0,0,0,0.87)' : '#FFF',
							'& .MuiChip-icon, & .MuiChip-deleteIcon': {
								color: isDark ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.7)',
							},
						},
					},
					colorInfo: {
						'&.MuiChip-filled': {
							color: isDark ? 'rgba(0,0,0,0.87)' : '#FFF',
							'& .MuiChip-icon, & .MuiChip-deleteIcon': {
								color: isDark ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.7)',
							},
						},
					},
				},
			},
			MuiBadge: {
				styleOverrides: {
					anchorOriginTopRightRectangular: {
						background: palette.background.badge,
						color: palette.text.primary,
					},
				},
			},
			MuiFormControl: {
				defaultProps: {
					variant: 'outlined',
				},
			},
			MuiFormHelperText: {
				styleOverrides: {
					root: {
						marginLeft: 0,
						marginRight: 0,
					},
				},
			},
			MuiAccordion: {
				styleOverrides: {
					root: {
						background: 'none',
					},
				},
			},
			MuiAccordionSummary: {
				styleOverrides: {
					root: {
						background: 'none',
						borderBottom: 'none',
						boxShadow: 'none',
					},
				},
			},
			MuiLink: {
				styleOverrides: {
					root: {
						color: palette.primary.main,
						cursor: 'pointer',
						fontSize: '0.8125rem',
						fontWeight: 500,
						lineHeight: '2rem',
						textDecoration: 'underline',
					},
				},
			},
			MuiButton: {
				styleOverrides: {
					root: {
						color: isDark ? '#000' : '#FFF',
						whiteSpace: 'nowrap',
						fontWeight: 600,
						letterSpacing: 0,
					},
					contained: {
						background: palette.primary.main,
						boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
						':hover': {
							background: isDark ? 'rgb(55, 180, 99)' : '#0E6533',
							boxShadow: '0 2px 6px rgba(0,0,0,0.35)',
						},
						':disabled': {
							background: isDark ? '#FFFFFF0F' : 'rgba(0,0,0,0.06)',
							boxShadow: 'none',
						},
					},
					outlined: {
						border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'}`,
						color: palette.primary.main,
						':hover': {
							border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.35)'}`,
							background: isDark
								? 'rgba(68, 197, 113, 0.06)'
								: 'rgba(17, 129, 61, 0.06)',
						},
					},
					outlinedError: {
						color: palette.fonts.error,
						':hover': {
							border: `2px solid ${palette.borderColors.error}`,
						},
					},
					text: {
						color: palette.primary.main,
						cursor: 'pointer',
						fontSize: '0.8125rem',
						fontWeight: 500,
						lineHeight: '2rem',
						textDecoration: 'underline',
					},
				},
			},
			MuiAlert: {
				styleOverrides: {
					standardInfo: {
						color: '#5FA9F2',
						'& .MuiAlert-icon': {
							color: '#5FA9F2',
						},
					},
					filledInfo: {
						color: isDark ? '#FFF' : '#0E6533',
						'& .MuiAlert-icon': {
							color: isDark ? '#FFF' : '#0E6533',
						},
					},
				},
			},
			MuiCssBaseline: {
				styleOverrides: {
					a: {
						textDecoration: 'none',
						color: palette.primary.main,
					},
				},
			},
		},
	})
}
